import recruitRepositoryV1 from "./recruit.repository.v1";

class RecruitServiceV1 {
  constructor(repository) {
    this.repository = repository;
  }

  calculateDDay(closingDate) {
    // 날짜 비교를 위해 시간 부분을 제거하고 날짜만 사용
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const closingDay = new Date(closingDate);
    closingDay.setHours(0, 0, 0, 0);

    const diffTime = closingDay.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 7 && diffDays >= 0) {
      return diffDays === 0 ? "D-Day" : `D-${diffDays}`;
    }
    return null;
  }

  splitDescription(description) {
    if (!description || typeof description !== "string") {
      return [];
    }
    return description.split("\n").map((item) => item.trim());
  }

  // formatYearOfExperience(min, max, careerLevel) {
  //   if (!min && !max) {
  //     return careerLevel;
  //   }
  //   if (!min) {
  //     return `${max}년 이상`;
  //   }
  //   if (!max) {
  //     return `${min}년 이하`;
  //   }
  //   return min === max ? `${min}년` : `${min}년 ~ ${max}년`;
  // }

  formatSalary(min, max) {
    if (!min && !max) {
      return "협의";
    }
    if (!min) {
      return `${max}만원 이상`;
    }
    if (!max) {
      return `${min}만원 이하`;
    }
    return min === max ? `${min}만원` : `${min}만원 ~ ${max}만원`;
  }

  removeHtmlTags(text) {
    if (!text || typeof text !== "string") {
      return "";
    }
    return text.replace(/<[^>]*>/g, "").replace(/&amp;/g, "&");
  }

  removeHtmlTagsFromArray(items) {
    if (!Array.isArray(items)) {
      return [];
    }

    const getIndentationLevel = (text) => {
      const ulMatch = text.match(/<ul/g);
      const liMatch = text.match(/<li/g);
      const ulCount = ulMatch ? ulMatch.length : 0;
      const liCount = liMatch ? liMatch.length : 0;
      return ulCount + liCount;
    };

    return items
      .map((item) => {
        const indentLevel = getIndentationLevel(item);
        const cleanText = this.removeHtmlTags(item);
        // <br>로 구분된 항목을 별도의 항목으로 분리하고 들여쓰기 추가
        return cleanText
          .split("<br>")
          .map((part) => part.trim())
          .filter((part) => part)
          .map((part) => "  ".repeat(indentLevel) + "• " + part);
      })
      .flat();
  }

  /**
   * 채용 공고 목록 조회
   * @param {*} query :{
   *  page?: number,
   *  limit?: number,
   *  search?: string, //검색어 (제목, 설명 등)
   *  keyword?: string,//검색 키워드 (제목, 내용, 요구사항 등)
   *  status?: '모집 중' | '마감됨' | '초안' | '모집 준비 중' | '수정 불가',
   *  startDate?: string,
   *  endDate?: string,
   *  careerLevel?: '신입' | '경력' | '전체', //전체로 들어오는 값을 무관으로 변환
   *  recruitmentType?: '상시' | '기간' | '전체', //'regular' | 'period' | 'all'으로 변경
   *  sortBy?: 'title' | 'workLocation' | 'status' | 'postedDate' | 'endDate' | 'createdAt' | 'updatedAt' | 'enrolledApplicants',
   *  order?: 'asc' | 'desc',
   * }
   * @returns
   */
  async getRecruitList(query) {
    const RECRUITMENT_TYPE_MAP = {
      상시: "regular",
      기간: "period",
      전체: "all",
    };
    const CAREER_LEVEL_MAP = {
      신입: "ENTRY",
      경력: "EXPERIENCED",
      무관: "ANY",
      전체: undefined,
    };
    const STATUS_MAP = {
      "모집 중": "RECRUITING",
      마감됨: "CLOSED",
    };
    const CAREER_LEVEL_DISPLAY = {
      ENTRY: "신입",
      EXPERIENCED: "경력",
      ANY: "무관",
    };
    const formattedQuery = {
      ...query,
      status: STATUS_MAP[query.status] ?? query.status,
      careerLevel: CAREER_LEVEL_MAP[query.careerLevel] ?? query.careerLevel,
      recruitmentType:
        query.recruitmentType === "전체"
          ? "all"
          : RECRUITMENT_TYPE_MAP[query.recruitmentType],
    };

    const recruitList = await this.repository.getRecruitList(formattedQuery);

    const items = recruitList.data.map((recruit) => ({
      id: recruit.id,
      title: recruit.title || "공고명이 없습니다.",
      description: `${
        recruit.endDate
          ? new Date(recruit.endDate).toLocaleDateString()
          : "상시채용"
      } | ${CAREER_LEVEL_DISPLAY[recruit.careerLevel] || recruit.careerLevel || "-"}`,
      dDate: recruit.endDate
        ? this.calculateDDay(new Date(recruit.endDate))
        : null,
    }));
    return {
      items,
      rangeSize: recruitList.totalPages === 1 ? 1 : 5,
      first: recruitList.totalPages === 1 ? true : false,
      last: recruitList.totalPages === query.page ? true : false,
      totalPages: recruitList.totalPages,
    };
  }

  async getRecruitDetail(id) {
    const recruitDetail = await this.repository.getRecruitDetail(id);
    if (!recruitDetail) {
      throw new Error("채용 공고 상세 정보를 찾을 수 없습니다.");
    }
    return {
      id: recruitDetail.id,
      title: recruitDetail.title || "공고명이 없습니다.",
      recruitImg: recruitDetail.fileUrl || null,
      positionPostType: recruitDetail.fileUrl ? "IMAGE" : "TEXT",
      contents: [
        {
          title: "회사/직무 소개",
          description: this.splitDescription(
            this.removeHtmlTags(
              recruitDetail.companyAndJobDescription ||
                "회사/직무 소개 정보가 없습니다."
            )
          ),
        },
        {
          title: "담당 업무",
          description:
            recruitDetail.responsibilities?.split("<br>") ||
            "담당 업무 정보가 없습니다.",
        },
        {
          title: "자격 요건",
          description:
            recruitDetail.qualifications?.split("<br>") ||
            "자격 요건 정보가 없습니다.",
        },
        {
          title: "우대 사항",
          description:
            recruitDetail.preferredQualifications?.split("<br>") ||
            "우대 사항 정보가 없습니다.",
        },
        {
          title: "혜택 및 복지",
          description:
            recruitDetail.benefitsAndPerks?.split("<br>") ||
            "혜택 및 복지가 없습니다.",
        },
        {
          title: "기술 스택 및 툴",
          description:
            recruitDetail.techStackAndTools?.split("<br>") ||
            "기술 스택 및 툴이 없습니다.",
        },
      ],
      stage: {
        title: "채용 전형 안내",
        description: `${(
          recruitDetail.stageCategories?.flatMap((category) =>
            category.stages.map((stage) => stage.name)
          ) || []
        ).join(" → ")} →  채용 확정`,
      },
      info: {
        careerLevel: { ENTRY: "신입", EXPERIENCED: "경력", ANY: "무관" }[recruitDetail.careerLevel] || recruitDetail.careerLevel,
        positionType: recruitDetail.isIntern ? "인턴" : "정규직",
        deadline: recruitDetail.endDate
          ? new Date(recruitDetail.endDate).toLocaleDateString()
          : "상시채용",
        location: recruitDetail.workLocation || "근무지 정보가 없습니다.",
        salary: this.formatSalary(
          recruitDetail.salaryMin,
          recruitDetail.salaryMax
        ),
      },
    };
  }

  async applyRecruitWithTempFile(data, recruitmentId, resumeUrl, portfolioUrl) {
    // 파일 유효성 검증
    if (
      !data.resume?.resume ||
      !(data.resume.resume instanceof FileList) ||
      data.resume.resume.length === 0
    ) {
      throw new Error("이력서 파일이 유효하지 않습니다.");
    }

    // S3에 파일 업로드
    await this.repository.uploadFileToS3(data.resume.resume[0], resumeUrl);

    if (portfolioUrl && data.resume?.portfolio?.[0]) {
      await this.repository.uploadFileToS3(data.resume.portfolio[0], portfolioUrl);
    }

    const requestData = {
      name: data.basicInfo.name,
      email: data.basicInfo.email,
      phone: data.basicInfo.phone,
      experienceType: data.basicInfo.experience.isNew ? "신입" : "경력",
      experienceYears: data.basicInfo.experience.years,
      accessRouteId: data.basicInfo.applyPath,
      recruitmentId: recruitmentId,
      isRequiredInfoAgreed: data.terms.personalInfo,
      isOptionalInfoAgreed: data.terms.optionalInfo,
      isSensitiveInfoAgreed: data.terms.sensitiveInfo,
      tempResumeUrl: resumeUrl,
      tempPortfolioUrl: portfolioUrl ?? undefined,
      answers: data.answers,
    };

    const response = await this.repository.applyRecruitWithTempFile(
      requestData
    );
    return response;
  }

  async postFileTempUploadUrl(file, fileType) {
    const requestData = {
      fileName: file.name,
      fileType: fileType,
    };

    const response = await this.repository.postFileTempUploadUrl(requestData);
    return response;
  }

  async getApplyAccessRoutes() {
    const accessRoutes = await this.repository.getApplyAccessRoutes();

    return accessRoutes.map((route) => ({
      id: route.id,
      name: route.name,
    }));
  }

  async getRecruitRecent(count) {
    const recruitList = await this.repository.getRecruitList({
      status: "RECRUITING",
    });

    if (recruitList.data.length === 0) {
      return [];
    }

    return recruitList.data.slice(0, count).map((recruit) => ({
      recruitStatus: recruit.status,
      department: "",
      id: recruit.id,
      title: recruit.title,
    }));
  }
}

const recruitServiceV1 = new RecruitServiceV1(recruitRepositoryV1);

export default recruitServiceV1;
